import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Temporal } from 'temporal-polyfill';
import * as yup from 'yup';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { FormFieldsContainer } from '../../../components/Form/FormComponents';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { FormFieldAndLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { schemaToFormFields } from '../../../util/schema-utils';
import { IFormField } from '../../../view-models/form.view-model';
import { RendererType } from '../../../data-models/field-definition';
import { transactionReconAtom } from '../state/FinanceStateJotai';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { getErrorMessage } from '../../../services/queryHelpers';
import {
  getPresignedUrlsBatch,
  uploadToS3,
  getUploadStatus,
  getStepLabel,
  isProcessingComplete,
  PresignRequest,
} from '../../../services/queries/SoiAgentQueries';
import { MAX_FILE_SIZE, MAX_BATCH_UPLOAD_SIZE } from '../../../constants/file-upload.constants';
import { RECON_FILES_KEY } from '../../AdminPanel/ReconciliationMonitor/hooks/useReconciliationMonitor';

interface AddCartaSoiModalProps {
  open: boolean;
  onClose: () => void;
}

const soiFileTypes = [
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

const soiUploadSchema = yup.object({
  files: yup
    .mixed()
    .required('Please select one or more files')
    .test(
      'is-valid-count',
      `Cannot upload more than ${MAX_BATCH_UPLOAD_SIZE} files at once. Please select ${MAX_BATCH_UPLOAD_SIZE} or fewer files.`,
      (value) => {
        if (Array.isArray(value)) {
          return value.length <= MAX_BATCH_UPLOAD_SIZE;
        }
        return true;
      }
    )
    .test('is-valid-size', `Max file size is ${MAX_FILE_SIZE / (1024 * 1024)}MB per file`, (value) => {
      if (!value) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.every((file: File) => file.size <= MAX_FILE_SIZE);
      }
      return (value as File).size <= MAX_FILE_SIZE;
    })
    .test('is-valid-type', 'Invalid file type', (value) => {
      if (!value) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.every((file: File) => soiFileTypes.includes(file.type));
      }
      return soiFileTypes.includes((value as File).type);
    })
    .default(null)
    .meta({
      renderer: RendererType.fileUpload,
      rendererMeta: {
        acceptTypes: soiFileTypes,
        acceptExtensions: ['csv', 'xlsx', 'xls'],
        multiple: true,
      },
    }),
});

type SoiUploadPayload = yup.InferType<typeof soiUploadSchema>;

// Background polling interval (5s is less aggressive than detail view's 2s)
const BACKGROUND_POLL_INTERVAL_MS = 5000;

export const AddCartaSoiModal: FC<AddCartaSoiModalProps> = ({ open, onClose }) => {
  const refreshReconData = useSetAtom(transactionReconAtom);
  const queryClient = useQueryClient();
  const { pushInfoToast, pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const methods = useForm<SoiUploadPayload>({
    resolver: yupResolver(soiUploadSchema),
  });

  const fieldsMap = useMemo(() => {
    return schemaToFormFields(soiUploadSchema).reduce(
      (map, field) => map.set(field.key, field),
      new Map<string, IFormField<unknown>>()
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    const { files } = methods.getValues();

    if (!files) {
      return;
    }

    setLoading(true);

    // Abort any existing polling before starting new uploads
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const fileArray: File[] = Array.isArray(files) ? files : [files];
      const dateStr = Temporal.Now.plainDateISO().toString();

      // Step 1: Get presigned URLs
      pushInfoToast({ message: `Preparing upload for ${fileArray.length} file(s)...` });

      const presignRequests: PresignRequest[] = fileArray.map((file) => ({
        fileName: file.name,
        period: dateStr,
      }));

      const presignResponse = await getPresignedUrlsBatch(presignRequests);

      // Step 2: Upload files directly to S3
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const { signedUrl } = presignResponse.uploads[i];
        const fileIndex = i + 1;
        const totalFiles = fileArray.length;

        pushInfoToast({
          message: `Uploading file ${fileIndex} of ${totalFiles}: ${file.name}...`,
        });

        await uploadToS3(signedUrl, file);
      }

      // Processing is triggered automatically by S3 → SQS
      const uploadIds = presignResponse.uploads.map((u) => u.uploadId);

      // Close modal immediately - processing happens in background
      pushInfoToast({
        message: `Processing ${fileArray.length} file(s) in background. This typically takes 2-3 minutes.`,
      });

      onClose();

      // Poll for completion in background - all uploads concurrently
      const pollSingleUpload = async (uploadId: number) => {
        let status = await getUploadStatus(uploadId);
        let lastStepCount = 0;

        while (!isProcessingComplete(status.status) && !abortController.signal.aborted) {
          await new Promise((resolve) => setTimeout(resolve, BACKGROUND_POLL_INTERVAL_MS));

          if (abortController.signal.aborted) {
            break;
          }

          status = await getUploadStatus(uploadId);

          // Show progress for all new steps (not just the latest)
          if (status.steps && status.steps.length > lastStepCount) {
            const newSteps = status.steps.slice(lastStepCount);
            newSteps.forEach((step) => {
              const stepLabel = getStepLabel(
                step.stepType,
                status.agentInput?.fileIndex,
                status.agentInput?.totalFiles
              );
              pushInfoToast({ message: `${status.fileName}: ${stepLabel}` });
            });
            lastStepCount = status.steps.length;
          }
        }

        if (abortController.signal.aborted) {
          return;
        }

        if (status.status === 'failed') {
          pushErrorToast({
            message: `Processing failed for ${status.fileName}: ${status.errorMessage || 'Unknown error'}`,
          });
        } else {
          const summary = status.results?.summary;
          pushSuccessToast({
            message: `${status.fileName}: Complete! ${summary?.extractedCount || 0} companies extracted.`,
          });
        }
      };

      // Poll all uploads concurrently
      const pollInBackground = async () => {
        try {
          await Promise.all(uploadIds.map(pollSingleUpload));

          if (!abortController.signal.aborted) {
            // Invalidate SOI queries so Monitor page shows fresh data
            queryClient.invalidateQueries({ queryKey: [RECON_FILES_KEY] });
            // Refresh the recon data grid
            refreshReconData(RESET);
          }
        } catch (err) {
          if (!abortController.signal.aborted) {
            console.error('Background polling error:', err);
          }
        }
      };

      // Fire and forget - don't await
      pollInBackground();
    } catch (error) {
      console.error('Upload failed:', error);
      pushErrorToast({ message: getErrorMessage(error, 'Failed to upload SOI files') });
    } finally {
      setLoading(false);
    }
  }, [methods, refreshReconData, queryClient, pushInfoToast, pushErrorToast, pushSuccessToast, onClose]);

  const handleClose = useCallback(() => {
    methods.reset();
    onClose();
  }, [methods, onClose]);

  return (
    <BasicDialog open={open} onClose={handleClose} title='Upload SOI Files'>
      <FormFieldsContainer>
        {open && (
          <FormProvider {...methods}>
            <FormFieldAndLabelFactory formField={fieldsMap.get('files')!} />

            <StickyFormButtons
              onCancel={handleClose}
              onSubmit={() => methods.handleSubmit(handleSubmit)()}
              loading={loading}
              submitLabel='Upload SOI Files'
              style={{ padding: '0.5rem 0 2rem' }}
            />
          </FormProvider>
        )}
      </FormFieldsContainer>
    </BasicDialog>
  );
};
